/**
 * use-dashboard-data.ts — TanStack Query hook for the home dashboard.
 *
 * Wraps useOnboardingPackages (already ships enriched rows: completeness,
 * trust, reviewer, blockerCount). No new API endpoints — derives all
 * dashboard counters client-side from the list response.
 *
 * Fetches with a high limit (50) to cover the full workspace in one shot.
 * If total > 50 in future, pagination can be added here without changing
 * the dashboard components (they receive a flat packages array).
 *
 * GH-268: returns BOTH the full list (`allPackages`) and a filtered view
 * (`packages`) that excludes `isTemplate=true` packages by default. The
 * caller decides which one to feed counters/lists based on the
 * `includeSampleData` toggle state. We intentionally do not duplicate the
 * filtering logic in each consumer — the hook is the single source of truth.
 */

import { useMemo } from 'react'
import { useOnboardingPackages } from '../packages/use-onboarding-packages'
import type { OnboardingPackage } from '@agentwiki/shared'

export interface DashboardData {
  /** Real (non-template) packages — default counter source. */
  packages: OnboardingPackage[]
  /** Full list including DEMO templates — used when toggle is ON. */
  allPackages: OnboardingPackage[]
  /** Convenience: number of `isTemplate=true` packages currently loaded. */
  templateCount: number
  isLoading: boolean
  isError: boolean
}

export interface UseDashboardDataOptions {
  /**
   * When true, `packages` includes templates as well. Defaults to false so
   * counters reflect real onboarding work and don't get inflated by demo data.
   */
  includeSampleData?: boolean
}

export function useDashboardData(options: UseDashboardDataOptions = {}): DashboardData {
  const { includeSampleData = false } = options
  const { data, isLoading, isError } = useOnboardingPackages({ limit: 50, offset: 0 })

  const allPackages = data?.items ?? []

  const packages = useMemo(
    () => (includeSampleData ? allPackages : allPackages.filter((p) => !p.isTemplate)),
    [allPackages, includeSampleData],
  )

  const templateCount = useMemo(
    () => allPackages.filter((p) => p.isTemplate).length,
    [allPackages],
  )

  return {
    packages,
    allPackages,
    templateCount,
    isLoading,
    isError,
  }
}
